import { auth } from "../../services/firebase";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { CheckCircle2, MessageSquare, Send } from "lucide-react";
import { toast } from "react-hot-toast";
import Drill from "@drillflo/drillcore/lib/Drill";
import { PlaybackData } from "../hooks/usePlayback";
import { UserData } from "../hooks/useUser";

interface Comment {
  content: string;
  user: string;
  userId: string;
  isCompleted: boolean;
  timestamp: string;
  replies: {
    content: string;
    user: string;
    userId: string;
    timestamp: string;
  }[];
}

const Comments = ({
  drill,
  playbackState,
  currentUser,
}: {
  drill: Drill | null;
  playbackState: PlaybackData;
  currentUser: UserData | null;
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState("");
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);

  const filteredUsers: { id: string; name: string; email: string }[] = [];
  // const filteredUsers = collaborators?.filter((user) =>
  //   // user.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
  //   user.email.toLowerCase().includes(mentionSearch.toLowerCase())
  // );

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const matches = textBeforeCursor.match(/@(\w*)$/);

    if (matches) {
      setMentionSearch(matches[1]);
      setShowSuggestions(true);
      setSuggestionIndex(0);

      // Calculate position for suggestions popup
      const textArea = textareaRef.current;
      if (textArea) {
        const { offsetLeft, offsetTop } = textArea;
        setCursorPosition({
          left: offsetLeft + 10,
          top: offsetTop + 30,
        });
      }
    } else {
      setShowSuggestions(false);
    }

    // Extract mentions from text
    // const mentions =
    //   newValue.match(/@\[(.*?)\]/g)?.map((mention) => {
    //     const user = collaborators.find((u) => `@[${u.name}]` === mention);
    //     return user?.id || "";
    //   }) || [];

    handleTextChange(newValue, []);
  };

  const insertMention = (user: { id: string; name: string; email: string }) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPos);
    const textAfterCursor = text.slice(cursorPos);
    const mentionText = `@${user.email}`;

    // Replace the @mention being typed with the full mention
    const newText =
      textBeforeCursor.replace(/@\w*$/, mentionText) + textAfterCursor;

    handleTextChange(newText, [...(text.match(/@\[(.*?)\]/g) || []), user.id]);
    setShowSuggestions(false);
  };

  const getCurrentPage = useCallback(() => {
    if (!drill) return null;

    const currentPageIndex =
      playbackState.currentPage ?? drill.selectedPageIndex ?? 0;

    return drill.pages.length > currentPageIndex
      ? drill.pages[currentPageIndex]
      : null;
  }, [drill, playbackState]);

  useEffect(() => {
    const currentPage = getCurrentPage();

    if (!currentPage) return;

    const rawComments = (currentPage.getCustomData("comments") ||
      []) as Comment[];

    setComments(
      rawComments.sort((a, b) => {
        return (
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      })
    );
  }, [drill, playbackState, getCurrentPage]);

  const handleSubmitComment = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!text.trim()) return;
      const currentPage = getCurrentPage();

      if (!currentPage) {
        toast.error("No page selected");
        return;
      }

      const comment: Comment = {
        content: text,
        user: currentUser?.email ?? "Unknown User",
        userId: auth.currentUser?.uid || "",
        isCompleted: false,
        timestamp: new Date().toISOString(),
        replies: [],
      };

      setComments((prevComments) => {
        const updatedComments = [comment, ...prevComments];
        currentPage.setCustomData("comments", updatedComments);
        return updatedComments;
      });
      setNewComment("");
    },
    [currentUser, text, getCurrentPage]
  );

  const handleTextChange = (newText: string, newMentions: string[]) => {
    setText(newText);
    setMentions(newMentions);
  };

  const handleReply = useCallback(
    async (replyToComment: Comment) => {
      if (!replyContent.trim()) return;

      const currentPage = getCurrentPage();

      if (!currentPage) {
        toast.error("No page selected");
        return;
      }

      setComments((prevComments) => {
        const updatedComments = prevComments.map((comment) => {
          if (comment === replyToComment) {
            return {
              ...comment,
              replies: [
                ...comment.replies,
                {
                  user: currentUser?.email ?? "Unknown User",
                  userId: auth.currentUser?.uid || "",
                  content: replyContent,
                  timestamp: new Date().toISOString(),
                },
              ],
            };
          }
          return comment;
        });

        currentPage.setCustomData("comments", updatedComments);
        return updatedComments;
      });

      setReplyContent("");
      setReplyingTo(null);
    },
    [replyContent, currentUser, getCurrentPage]
  );

  const toggleComplete = useCallback(
    async (comment: Comment) => {
      const currentPage = getCurrentPage();

      if (!currentPage) {
        toast.error("No page selected");
        return;
      }

      setComments((prevComments) => {
        const updatedComments = prevComments.map((c) => {
          if (c === comment) {
            return { ...c, isCompleted: !c.isCompleted };
          }
          return c;
        });

        currentPage.setCustomData("comments", updatedComments);
        return updatedComments;
      });
    },
    [getCurrentPage]
  );

  return (
    <div className="z-40 mx-1   transition-transform">
      <h3 className="text-lg font-semibold mb-2">Comments</h3>
      <div className="flex flex-col h-full min-h-full">
        <div className="comments-list h-3/6 overflow-scroll max-h-full">
          {comments.map((comment) => (
            <div
              key={comment.timestamp}
              className={`border border-gray-200 ${
                comment.replies && comment.replies?.length > 0 ? "mb-4" : ""
              }`}
            >
              <div
                className={`rounded-lg  p-2 shadow-sm transition-colors duration-75 ease-linear ${
                  comment.isCompleted
                    ? "bg-slate-100 text-white  overflow-clip"
                    : "bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-4 flex-col">
                  <div className="flex-1">
                    <div className="flex items-start flex-col text-left justify-left gap-2 mb-2">
                      <span className="font-medium text-xs text-gray-900">
                        {comment.user}
                      </span>

                      <span className=" text-xs text-left text-gray-500">
                        {new Date(comment.timestamp).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    </div>
                    <p
                      className={`mt-1 text-gray-700 text-left max-w-full overflow-wrap text-sm ${
                        comment.isCompleted ? "text-gray-500" : ""
                      }`}
                    >
                      {comment.content}
                    </p>
                  </div>

                  <div className="flex items-center ">
                    <button
                      onClick={() => toggleComplete(comment)}
                      className={`rounded-full p-1 transition-colors text-sm ${
                        comment.isCompleted
                          ? "text-green-600 hover:text-green-700"
                          : "text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      <CheckCircle2 size={20} />
                    </button>
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-4">
                  <button
                    onClick={() =>
                      setReplyingTo(replyingTo === comment ? null : comment)
                    }
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <MessageSquare size={16} />
                    Reply
                  </button>
                </div>

                {replyingTo === comment && (
                  <div className="mt-3">
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="w-full resize-none rounded-lg border border-gray-200 bg-white p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      rows={1}
                      placeholder="Write a reply..."
                    />
                    <button
                      onClick={() => handleReply(comment)}
                      className="mt-2 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                      disabled={!replyContent.trim()}
                    >
                      Reply
                    </button>
                  </div>
                )}

                {comment.replies && comment.replies.length > 0 && (
                  <div className="mt-1 space-y-1 text-left border-l-2 border-purple-400">
                    {comment.replies.map((reply, index) => (
                      <div
                        key={index}
                        className="ml-2 rounded-lg bg-gray-100 p-2 text-sm"
                      >
                        <div className="flex items-left flex-col ">
                          <span className="font-medium text-xs text-gray-900 overflow-clip">
                            {reply.user}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(reply.timestamp).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        </div>
                        <p className="mt-1 text-gray-700">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {comments.length == 0 && (
            <div>
              <span className="font-normal text-sm text-gray-600">
                {" "}
                No comments yet
              </span>
              <p className=" font-light pt-4 text-sm text-gray-600">
                Have a thought? Add a comment here to start a conversation
              </p>
            </div>
          )}
        </div>
        <div className=" min-w-full w-full  mx-auto space-y-6">
          <div className="rounded-lg bg-white shadow-sm w-full min-w-full p-2 m-2 m-auto gap-4 flex flex-col">
            <h2 className="text-md font-semibold mb-4">New Comment</h2>
            <form
              onSubmit={handleSubmitComment}
              className="w-full flex flex-row w-full gap-2"
            >
              <div className="relative w-full">
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={handleInput}
                  placeholder={"Add comment"}
                  className="flex-1 resize-none rounded-lg border border-gray-200 bg-white p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                  rows={1}
                />

                {showSuggestions && filteredUsers.length > 0 && (
                  <div
                    className="absolute z-10 mt-1 w-64 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
                    style={{
                      top: cursorPosition.top,
                      left: cursorPosition.left,
                    }}
                  >
                    <ul className="max-h-60 overflow-auto py-1 text-base">
                      {filteredUsers.map((user, index) => (
                        <li
                          key={user.id}
                          className={`cursor-pointer px-3 py-2 hover:bg-gray-100 ${
                            index === suggestionIndex ? "bg-gray-100" : ""
                          }`}
                          onClick={() => insertMention(user)}
                        >
                          <div className="flex items-center">
                            <span className="font-medium">{user.name}</span>
                            <span className="ml-2 text-sm text-gray-500">
                              {user.email}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={!text.trim()}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className=" w-12" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Comments;
